import { useEffect, useMemo, useRef, useState } from "react";
import type { Language } from "../api";
import { prefersReducedMotion } from "../theme";

/* ═══════════════════════════════════════════════════════════════════════
   Choosing a language.

   For the people this exists for, this is the moment they decide whether
   the product is for them. So the prompt cycles through the languages most
   likely to be needed, in their own script — you should be able to
   recognise your language before you can read a word of the page.
   ═══════════════════════════════════════════════════════════════════════ */

const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Higher is better; 0 means no match. Mirrors the server's ranking. */
function score(l: Language & { search?: string }, query: string): number {
  const q = fold(query);
  if (!q) return 1;
  const label = fold(l.label);
  const native = fold(l.native);
  const alias = fold(l.search ?? "");

  if (label === q || native === q) return 100;
  if (label.startsWith(q) || native.startsWith(q)) return 80;
  const words = `${alias} ${label} ${native}`.split(/\s+/);
  if (words.includes(q)) return 70;
  if (label.includes(q) || native.includes(q)) return 55;
  if (words.some((w) => w.startsWith(q))) return 45;
  if (alias.includes(q)) return 30;
  return 0;
}

/** Chosen to reflect who actually walks through the door. */
const ROTATE = ["en", "fil", "zh", "vi", "pa", "ar", "hi", "ne", "ko", "es", "yue", "th", "id", "ta"];

export function LanguagePicker({
  languages,
  onPick,
  showVoiceBadges,
  prompt,
  note,
}: {
  languages: (Language & { search?: string })[];
  onPick(code: string): void;
  showVoiceBadges: boolean;
  /** The pack's own wording, where the default is ambiguous. See PackCopy. */
  prompt?: string;
  note?: string;
}) {
  const [query, setQuery] = useState("");
  const [rot, setRot] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const rotation = useMemo(
    () => ROTATE.map((c) => languages.find((l) => l.code === c)).filter(Boolean) as Language[],
    [languages],
  );

  useEffect(() => {
    if (prefersReducedMotion() || rotation.length === 0 || query) return;
    const id = setInterval(() => setRot((r) => (r + 1) % rotation.length), 1700);
    return () => clearInterval(id);
  }, [rotation.length, query]);

  const results = useMemo(() => {
    if (!query.trim()) return languages;
    return languages
      .map((l) => [l, score(l, query)] as const)
      .filter(([, s]) => s > 0)
      .sort((a, b) => b[1] - a[1] || a[0].label.localeCompare(b[0].label))
      .map(([l]) => l);
  }, [languages, query]);

  const current = rotation[rot] ?? rotation[0];

  /* The count belongs to the list, not to whoever wrote the sentence. This
     component is the only thing that knows it, so it fills it in. */
  const say = (s: string) => s.replace(/\{count\}/g, String(languages.length));

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="text-center mb-4">
        <p style={{ color: "var(--ink-2)", fontSize: 14 }}>
          {prompt ? say(prompt) : "What language would you like to use?"}
        </p>
        {/* Under the question, not the rotating word — it is answering
            "which language do you mean?", so it has to arrive with it. */}
        {note && (
          <p style={{ color: "var(--ink-3)", fontSize: 12.5, marginTop: 3, lineHeight: 1.45 }}>
            {say(note)}
          </p>
        )}
        <p
          key={rot}
          className="fade-in"
          style={{ fontSize: 20, fontWeight: 600, marginTop: 4, letterSpacing: "-0.01em" }}
        >
          {query ? " " : (current?.native ?? "English")}
        </p>
      </div>

      <input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={`Search ${languages.length} languages…`}
        className="field"
        aria-label="Search languages"
      />

      <div
        className="scroll-y mt-2"
        style={{
          maxHeight: 260,
          border: "1px solid var(--line)",
          borderRadius: "var(--radius)",
          background: "var(--surface)",
        }}
      >
        {results.length === 0 && (
          <p className="px-3 py-3" style={{ fontSize: 13, color: "var(--ink-3)" }}>
            No matches — try the English name.
          </p>
        )}
        {results.slice(0, 80).map((l, i) => (
          <button
            key={l.code}
            type="button"
            onClick={() => onPick(l.code)}
            className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-3"
            style={{
              borderTop: i === 0 ? "none" : "1px solid var(--line)",
              background: "transparent",
              cursor: "pointer",
              fontSize: 14,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-tint)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="truncate">{l.native}</span>
              {/* Only where a call would genuinely work. Marking every
                  language would be a promise we can't keep. */}
              {showVoiceBadges && l.voice && (
                <span
                  title="Voice available in this language"
                  aria-label="voice available"
                  style={{ fontSize: 11 }}
                >
                  🎙
                </span>
              )}
            </span>
            <span style={{ color: "var(--ink-3)", fontSize: 12.5 }} className="shrink-0">
              {l.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
